import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";

import { esES } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';
import { WebInfoProvider, useWebInfo } from './context/WebInfoContext';
import logger from "./utils/logger";

import Routes from "./routes";
import ErrorBoundary from "./components/ErrorBoundary";
import { buildAgendaTheme } from "./theme/agendaTheme";
import {
    temaInicial,
    obtenerTema,
    guardarModo,
    listarTemas,
    guardarTemaEmpresa,
    guardarAcento
} from "./theme/themeService";
import { applyTokens } from "./theme/tokensToCssVars";
// import DragDropTest from "./components/DragDropTest";

const queryClient = new QueryClient();

const App = () => {
    return (
        <ErrorBoundary>
            <WebInfoProvider>
                <AppContent />
            </WebInfoProvider>
        </ErrorBoundary>
    );
};

// ✅ Componente interno que usa useWebInfo (debe estar dentro del provider)
const AppContent = () => {
    const [locale, setLocale] = useState();

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    // ✅ Obtener información web desde DB (ahora desde contexto compartido)
    const { webInfo } = useWebInfo();

    // Log de prueba al cargar la aplicación
    useEffect(() => {
        logger.debug("🎯 App.js cargado - Aplicación iniciada");
        logger.whatsapp.debug("🧪 LOG DE PRUEBA - WhatsApp desde App.js");
        logger.socket.debug("🧪 LOG DE PRUEBA - Socket desde App.js");
    }, []);

    // ✅ Actualizar título cuando se carguen los datos desde DB (useWebInfo ya actualiza el título)
    // ✅ CORREGIDO: Usar websiteTitle directamente, no appName + appSubtitle
    useEffect(() => {
        if (webInfo?.title) {
            document.title = webInfo.title;
            const titleElement = document.getElementById('app-title');
            if (titleElement) {
                titleElement.textContent = webInfo.title;
            }
        }
    }, [webInfo]);

    // ✅ 0390 FT-4/FT-5: estado del sistema de temas.
    //
    // Va ANTES de `colorMode` a propósito: aquel lo referencia en su lista de
    // dependencias, y declararlo después provocaría un ReferenceError por zona
    // muerta temporal en el primer render.
    //
    // Esta fase sigue SIN tocar el objeto `theme` de Material-UI: los tokens solo
    // se publican como variables CSS. Ver la nota de FT-4 más abajo.
    const [tema, setTema] = useState(() => temaInicial());
    const [themes, setThemes] = useState([]);

    const themeTokens = tema.tokens;
    const themeSlug = tema.themeSlug;
    const themeId = tema.themeId ?? null;

    // ✅ 0390 — cómo se dibuja el menú lo DECLARA el tema, no su nombre.
    //
    // Antes se deducía comparando el slug con 'professional'. Al personalizar,
    // `sp_theme_clone` crea `company-N` y esa comparación pasaba a ser falsa
    // aunque el clon llevara dentro los valores profesionales: el menú salía
    // híbrido, con las medidas de uno y los rasgos del otro.
    const navStyle =
        (themeTokens.find(t => t.tokenKey === "nav-style") || {}).tokenValue ||
        "raised";

    const aplicarTema = React.useCallback((siguiente) => {
        if (siguiente && Array.isArray(siguiente.tokens)) {
            setTema(siguiente);
        }
    }, []);

    // ✅ 0390 FT-5: el contexto transporta además el tema activo y su selector.
    // Se conserva `toggleColorMode` con su firma original: lo llama layout/index.js.
    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => {
                    const siguiente = prevMode === "light" ? "dark" : "light";
                    // Persistencia en base, sin bloquear el cambio visual: el modo
                    // se aplica ya y la escritura viaja detrás. Si falla, queda
                    // guardado en localStorage como hasta ahora.
                    guardarModo(siguiente);
                    return siguiente;
                });
            },
            setMode: (siguiente) => {
                if (siguiente !== "light" && siguiente !== "dark") return;
                setMode(siguiente);
                guardarModo(siguiente);
            },
            mode,
            themeSlug,
            themeId,
            navStyle,
            themes,
            tokens: themeTokens,
            // Recarga tema Y lista. Lo invoca `ThemeSync` en cuanto hay sesión:
            // la primera carga ocurre sin token —el proveedor de sesión está por
            // debajo de este componente— y sin esta segunda pasada la lista de
            // temas quedaría vacía para el resto de la sesión.
            refreshTheme: async () => {
                const [tema, lista] = await Promise.all([
                    obtenerTema(),
                    listarTemas()
                ]);
                aplicarTema(tema);
                setThemes(lista);
                return tema;
            },
            // Cambia el tema de TODA la empresa. Propaga el error a propósito:
            // quien pulsa debe saber si no se guardó (ver themeService).
            setTheme: async (slug) => {
                const tema = await guardarTemaEmpresa(slug);
                aplicarTema(tema);
                return tema;
            },
            // Personalización del acento. Si el tema activo es del sistema, el
            // backend lo duplica y activa la copia; aquí solo se refresca.
            setAccent: async (modo, color) => {
                const tema = await guardarAcento(modo, color);
                aplicarTema(tema);
                const lista = await listarTemas();
                setThemes(lista);
                return tema;
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mode, themeSlug, themeId, themes, themeTokens, navStyle]
    );

    const theme = createTheme(
        {
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '8px',
                    height: '8px',
					borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                    backgroundColor: "#1e3a8a",
					borderRadius: "8px",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
					borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#F3F4F6" : "#374151",
					borderRadius: "8px",
                },
            },
            palette: {
                type: mode,
                primary: { main: mode === "light" ? "#1e3a8a" : "#3b82f6" },
                secondary: { main: "#ea0000" }, // ✅ COLOR PERSONALIZADO PARA GRUPOS
				sair: { main: mode === "light" ? "#1e3a8a" : "#374151" },
				vcard: { main: mode === "light" ? "#1e3a8a" : "#6b7280" },
                textPrimary: mode === "light" ? "#1f2937" : "#f9fafb",
                borderPrimary: mode === "light" ? "#1e3a8a" : "#3b82f6",
                dark: { main: mode === "light" ? "#374151" : "#f3f4f6" },
                light: { main: mode === "light" ? "#f9fafb" : "#374151" },
                tabHeaderBackground: mode === "light" ? "#f3f4f6" : "#374151",
                optionsBackground: mode === "light" ? "#ffffff" : "#1f2937",
				options: mode === "light" ? "#ffffff" : "#374151",
				fontecor: mode === "light" ? "#1e3a8a" : "#3b82f6",
                fancyBackground: mode === "light" ? "#f9fafb" : "#111827",
				bordabox: mode === "light" ? "#e5e7eb" : "#374151",
				newmessagebox: mode === "light" ? "#f3f4f6" : "#374151",
				inputdigita: mode === "light" ? "#ffffff" : "#374151",
				contactdrawer: mode === "light" ? "#ffffff" : "#374151",
				announcements: mode === "light" ? "#f3f4f6" : "#374151",
				login: mode === "light" ? "#ffffff" : "#111827",
				announcementspopover: mode === "light" ? "#ffffff" : "#374151",
				chatlist: mode === "light" ? "#f3f4f6" : "#374151",
				boxlist: mode === "light" ? "#f3f4f6" : "#374151",
				boxchatlist: mode === "light" ? "#f3f4f6" : "#1f2937",
                total: mode === "light" ? "#ffffff" : "#111827",
                messageIcons: mode === "light" ? "#6b7280" : "#d1d5db",
                inputBackground: mode === "light" ? "#ffffff" : "#374151",
                barraSuperior: mode === "light" ? "linear-gradient(to right, #1e3a8a, #3b82f6, #1e3a8a)" : "#1f2937",
				boxticket: mode === "light" ? "#f3f4f6" : "#374151",
				campaigntab: mode === "light" ? "#f3f4f6" : "#374151",
				mediainput: mode === "light" ? "#f3f4f6" : "#111827",
				// ✅ NUEVOS COLORES PARA MENSAJES - TEMA DARK
				messageBackground: mode === "light" ? "#f5f5f5" : "#111827",
				messageLeftBg: mode === "light" ? "#93c5fd" : "#1e40af", // Azul más oscuro para dark
				messageLeftText: mode === "light" ? "#000000" : "#ffffff",
				messageRightBg: mode === "light" ? "#6ee7b7" : "#059669", // Verde más oscuro para dark
				messageRightText: mode === "light" ? "#000000" : "#ffffff",
				quotedLeftBg: mode === "light" ? "#f0f0f0" : "#374151",
				quotedRightBg: mode === "light" ? "#cfe9ba" : "#065f46",
				quotedSideLeft: mode === "light" ? "#6bcbef" : "#3b82f6",
				quotedSideRight: mode === "light" ? "#35cd96" : "#10b981",
				dailyTimestampBg: mode === "light" ? "#e1f3fb" : "#1f2937",
				dailyTimestampText: mode === "light" ? "#808888" : "#9ca3af",
				messageContactNameColor: mode === "light" ? "#6bcbef" : "#60a5fa",
				messageDeletedText: mode === "light" ? "rgba(0, 0, 0, 0.36)" : "rgba(255, 255, 255, 0.5)",
				forwardMessageColor: mode === "light" ? "#999" : "#9ca3af",
            },
            mode,
            // ✅ FASE 0: Módulo Agenda — paleta, tipografía, variables (0257)
            // D1: mode-aware — antes inyectaba la paleta light fija en ambos temas
            agenda: buildAgendaTheme(mode),
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale =
            i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

        if (browserLocale === "esES") {
            setLocale(esES);
        }
    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);

    // ✅ 0390 FT-4: los tokens se publican como variables CSS en `:root`.
    //
    // El objeto `theme` de Material-UI de arriba NO se toca: el aspecto no debe
    // cambiar todavía. El shell rediseñado (FT-6) será el primero en consumirlas.
    //
    // No se redefine `palette` desde los tokens porque hoy NO son equivalentes:
    // `fancyBackground` es #f9fafb y `surface-sunken` es #f3f4f6; `background.paper`
    // en oscuro es #424242 y `surface` es #1f2937. Igualarlos ahora cambiaría el
    // aspecto de las 55 páginas de golpe, que es justo lo que esta fase evita.
    useEffect(() => {
        applyTokens(themeTokens, mode);
    }, [themeTokens, mode]);

    // Refresco en segundo plano. `obtenerTema` nunca rechaza: si no hay sesión o
    // el servidor falla, devuelve la instantánea y la aplicación sigue pintada.
    useEffect(() => {
        let vigente = true;

        obtenerTema().then(siguiente => {
            if (vigente) aplicarTema(siguiente);
        });

        listarTemas().then(lista => {
            if (vigente) setThemes(lista);
        });

        return () => {
            vigente = false;
        };
    }, [aplicarTema]);

    // El modo que llega de la base gana sobre el heredado de `localStorage`, salvo
    // cuando el usuario eligió 'system': ahí manda la preferencia del navegador,
    // que es como se comportaba el producto antes de existir esta columna.
    useEffect(() => {
        if (tema.userMode === "light" || tema.userMode === "dark") {
            setMode(tema.userMode);
        }
    }, [tema.userMode]);



    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                      <Routes />
                      {/* <DragDropTest /> */}
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
